import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';

import type { revealPropertyInput } from '@sorare/core/src/__generated__/globalTypes';
import { cardCountsFragment } from '@sorare/core/src/contexts/currentUser/queries';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';

import { CardPullExperienceView } from './CardPullExperienceView/index.tsx';
import type {
  CardPullExperience_cardPullConfig,
  CardPullExperience_cardShardsChest,
  CardPullExperience_currentUser,
  ConfirmCardPullMutation,
  ConfirmCardPullMutationVariables,
  PickPulledCardMutation,
  PickPulledCardMutationVariables,
  PullCardsMutation,
  PullCardsMutationVariables,
  RejectCardPullMutation,
  RejectCardPullMutationVariables,
  RevealPropertyMutation,
  RevealPropertyMutationVariables,
} from './__generated__/index.graphql.ts';

const PULL_CARDS_MUTATION = gql`
  mutation PullCardsMutation($input: pullCardsInput!, $sport: Sport!) {
    pullCards(input: $input) {
      currentUser {
        slug
        ...CardPullExperienceView_currentUser
      }
      errors {
        message
        code
      }
    }
  }
  ${CardPullExperienceView.fragments.currentUser}
` as TypedDocumentNode<PullCardsMutation, PullCardsMutationVariables>;

const PICK_PULLED_CARD_MUTATION = gql`
  mutation PickPulledCardMutation(
    $input: pickPulledCardInput!
    $sport: Sport!
  ) {
    pickPulledCard(input: $input) {
      currentUser {
        slug
        ...CardPullExperienceView_currentUser
      }
      errors {
        message
        code
      }
    }
  }
  ${CardPullExperienceView.fragments.currentUser}
` as TypedDocumentNode<PickPulledCardMutation, PickPulledCardMutationVariables>;

const REJECT_CARD_PULL_MUTATION = gql`
  mutation RejectCardPullMutation(
    $input: rejectCardPullInput!
    $sport: Sport!
  ) {
    rejectCardPull(input: $input) {
      currentUser {
        slug
        cardPull(sport: $sport) {
          id
        }
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<RejectCardPullMutation, RejectCardPullMutationVariables>;

const CONFIRM_CARD_PULL_MUTATION = gql`
  mutation ConfirmCardPullMutation(
    $input: confirmCardPullInput!
    $sport: Sport!
  ) {
    confirmCardPull(input: $input) {
      currentUser {
        slug
        cardPull(sport: $sport) {
          id
        }
        ...CurrentUserProvider_cardCounts
      }
      confirmedCard {
        slug
        ...CardPullExperienceView_anyCardInterface
      }
      errors {
        message
        code
      }
    }
  }
  ${CardPullExperienceView.fragments.anyCardInterface}
  ${cardCountsFragment}
` as TypedDocumentNode<
  ConfirmCardPullMutation,
  ConfirmCardPullMutationVariables
>;

const REVEAL_PROPERTY_MUTATION = gql`
  mutation RevealPropertyMutation(
    $input: revealPropertyInput!
    $sport: Sport!
  ) {
    revealProperty(input: $input) {
      currentUser {
        slug
        ...CardPullExperienceView_currentUser
      }
      errors {
        message
        code
      }
    }
  }
  ${CardPullExperienceView.fragments.currentUser}
` as TypedDocumentNode<RevealPropertyMutation, RevealPropertyMutationVariables>;

type Props = {
  cardPullConfigs: CardPullExperience_cardPullConfig[];
  cardShardsChest: CardPullExperience_cardShardsChest;
  currentUser: Nullable<CardPullExperience_currentUser>;
};

type ConfirmedCard = NonNullable<
  ConfirmCardPullMutation['confirmCardPull']
>['confirmedCard'];
const CardPullExperienceComponent = ({
  cardPullConfigs,
  cardShardsChest,
  currentUser,
  forceRemount,
}: Props & {
  forceRemount: () => void;
}) => {
  const {
    flags: { useRevealPropertyCurrency = false },
  } = useFeatureFlags();

  const { sport } = useSportContext();
  const [pullCards] = useMutation(PULL_CARDS_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const [pickPulledCard] = useMutation(PICK_PULLED_CARD_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const [rejectCardPull] = useMutation(REJECT_CARD_PULL_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const [confirmCardPull, { loading: confirmCardPullLoading }] = useMutation(
    CONFIRM_CARD_PULL_MUTATION,
    {
      showErrorsWithSnackNotification: true,
    }
  );
  const [revealProperty] = useMutation(REVEAL_PROPERTY_MUTATION, {
    showErrorsWithSnackNotification: true,
  });

  const [confirmedCard, setConfirmedCard] = useState<ConfirmedCard>();

  const onPullCards = async () => {
    const { data, errors } = await pullCards({
      variables: {
        input: { cardShardsChestId: cardShardsChest.id },
        sport,
      },
    });

    return {
      hasErrors: errors.length > 0 && !data?.pullCards?.currentUser?.cardPull,
    };
  };

  const onPickPulledCard = async ({
    cardPullCardId,
  }: {
    cardPullCardId: string;
  }) => {
    await pickPulledCard({
      variables: { input: { cardPullCardId }, sport },
    });
  };

  const onRejectCardPull = async ({ cardPullId }: { cardPullId: string }) => {
    await rejectCardPull({
      variables: { input: { cardPullId }, sport },
    });
  };

  const onConfirmCardPull = async ({ cardPullId }: { cardPullId: string }) => {
    const { data } = await confirmCardPull({
      variables: { input: { cardPullId }, sport },
    });
    const confirmedCardData = data?.confirmCardPull?.confirmedCard;
    if (confirmedCardData) {
      setConfirmedCard(confirmedCardData);
    }
  };

  const onRevealProperty = async ({
    cardPullId,
    revealPropertyCurrency,
    propertyName,
  }: revealPropertyInput) => {
    await revealProperty({
      variables: {
        input: {
          cardPullId,
          revealPropertyCurrency: useRevealPropertyCurrency
            ? revealPropertyCurrency
            : undefined,
          propertyName,
        },
        sport,
      },
    });
  };

  return (
    <CardPullExperienceView
      cardPullConfigs={cardPullConfigs}
      cardShardsChest={cardShardsChest}
      currentUser={currentUser}
      confirmedCard={confirmedCard}
      onPullCards={onPullCards}
      onPickPulledCard={onPickPulledCard}
      onRejectCardPull={onRejectCardPull}
      onConfirmCardPull={onConfirmCardPull}
      confirmCardPullLoading={confirmCardPullLoading}
      onRevealProperty={onRevealProperty}
      onCloseConfirmDialog={() => setConfirmedCard(undefined)}
      forceRemount={forceRemount}
    />
  );
};

export const CardPullExperience = (props: Props) => {
  const [key, setKey] = useState(0);

  const forceRemount = () => {
    setKey(prev => prev + 1);
  };
  return (
    <CardPullExperienceComponent
      key={key}
      {...props}
      forceRemount={forceRemount}
    />
  );
};

CardPullExperience.fragments = {
  cardPullConfig: gql`
    fragment CardPullExperience_cardPullConfig on CardPullConfig {
      ...CardPullExperienceView_cardPullConfig
    }
    ${CardPullExperienceView.fragments.cardPullConfig}
  ` as TypedDocumentNode<CardPullExperience_cardPullConfig>,
  currentUser: gql`
    fragment CardPullExperience_currentUser on CurrentUser {
      slug
      ...CardPullExperienceView_currentUser
    }
    ${CardPullExperienceView.fragments.currentUser}
  ` as TypedDocumentNode<CardPullExperience_currentUser>,
  cardShardsChest: gql`
    fragment CardPullExperience_cardShardsChest on CardShardsChest {
      id
      ...CardPullExperienceView_cardShardsChest
    }
    ${CardPullExperienceView.fragments.cardShardsChest}
  ` as TypedDocumentNode<CardPullExperience_cardShardsChest>,
};
